import { get } from '../helpers/api';

const API_BASE = '/reportes';

/**
 * Servicio para manejar las llamadas a la API de reportes
 * Basado en la documentación DOCUMENTACION_REPORTES.md
 */

/**
 * Obtiene el dashboard principal con estadísticas generales
 * @param {Object} params - Parámetros de filtro opcionales
 * @param {string} params.fecha_inicio - Fecha de inicio (YYYY-MM-DD)
 * @param {string} params.fecha_fin - Fecha de fin (YYYY-MM-DD)
 * @param {number} params.contacto_id - ID del contacto/agente específico
 * @param {string} params.periodo - Período predefinido (hoy, semana, mes, ultimos_7_dias, ultimos_30_dias)
 * @returns {Promise} Respuesta con datos del dashboard
 */
export const getDashboardData = async (params = {}) => {
  try {
    // Filtrar parámetros nulos o vacíos
    const filteredParams = Object.entries(params)
      .filter(([key, value]) => value !== null && value !== undefined && value !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    const queryString = new URLSearchParams(filteredParams).toString();
    const url = `${API_BASE}/dashboard/${queryString ? `?${queryString}` : ''}`;
    
    console.log('Llamando a API de dashboard:', url);
    return await get(url);
  } catch (error) {
    console.error('Error al obtener datos del dashboard:', error);
    throw error;
  }
};

/**
 * Obtiene análisis detallado de casos por estado
 * @param {Object} params - Parámetros de filtro opcionales
 * @returns {Promise} Respuesta con datos de casos por estado
 */
export const getCasosPorEstado = async (params = {}) => {
  try {
    const filteredParams = Object.entries(params)
      .filter(([key, value]) => value !== null && value !== undefined && value !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    const queryString = new URLSearchParams(filteredParams).toString();
    const url = `${API_BASE}/casos-por-estado/${queryString ? `?${queryString}` : ''}`;
    
    console.log('Llamando a API de casos por estado:', url);
    return await get(url);
  } catch (error) {
    console.error('Error al obtener casos por estado:', error);
    throw error;
  }
};

/**
 * Obtiene análisis de productividad de agentes
 * @param {Object} params - Parámetros de filtro opcionales
 * @returns {Promise} Respuesta con datos de productividad
 */
export const getProductividadAgentes = async (params = {}) => {
  try {
    const filteredParams = Object.entries(params)
      .filter(([key, value]) => value !== null && value !== undefined && value !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    const queryString = new URLSearchParams(filteredParams).toString();
    const url = `${API_BASE}/productividad-agentes/${queryString ? `?${queryString}` : ''}`;
    
    console.log('Llamando a API de productividad agentes:', url);
    return await get(url);
  } catch (error) {
    console.error('Error al obtener productividad de agentes:', error);
    throw error;
  }
};

/**
 * Obtiene casos por tipo
 * @param {Object} params - Parámetros de filtro opcionales
 * @returns {Promise} Respuesta con datos de casos por tipo
 */
export const getCasosPorTipo = async (params = {}) => {
  try {
    const filteredParams = Object.entries(params)
      .filter(([key, value]) => value !== null && value !== undefined && value !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    const queryString = new URLSearchParams(filteredParams).toString();
    const url = `${API_BASE}/casos-por-tipo/${queryString ? `?${queryString}` : ''}`;
    
    console.log('Llamando a API de casos por tipo:', url);
    return await get(url);
  } catch (error) {
    console.error('Error al obtener casos por tipo:', error);
    throw error;
  }
};

/**
 * Obtiene tendencias de casos
 * @param {Object} params - Parámetros de filtro opcionales
 * @returns {Promise} Respuesta con datos de tendencias
 */
export const getTendenciasCasos = async (params = {}) => {
  try {
    const filteredParams = Object.entries(params)
      .filter(([key, value]) => value !== null && value !== undefined && value !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    const queryString = new URLSearchParams(filteredParams).toString();
    const url = `${API_BASE}/tendencias-casos/${queryString ? `?${queryString}` : ''}`;
    
    console.log('Llamando a API de tendencias casos:', url);
    return await get(url);
  } catch (error) {
    console.error('Error al obtener tendencias de casos:', error);
    throw error;
  }
};

/**
 * Obtiene tiempo promedio de resolución
 * @param {Object} params - Parámetros de filtro opcionales
 * @returns {Promise} Respuesta con datos de tiempo promedio
 */
export const getTiempoPromedioResolucion = async (params = {}) => {
  try {
    const filteredParams = Object.entries(params)
      .filter(([key, value]) => value !== null && value !== undefined && value !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    const queryString = new URLSearchParams(filteredParams).toString();
    const url = `${API_BASE}/tiempo-promedio-resolucion/${queryString ? `?${queryString}` : ''}`;
    
    console.log('Llamando a API de tiempo promedio resolución:', url);
    return await get(url);
  } catch (error) {
    console.error('Error al obtener tiempo promedio de resolución:', error);
    throw error;
  }
};

/**
 * Exporta reporte a Excel
 * @param {string} tipo - Tipo de reporte (dashboard, casos-por-estado, productividad-agentes, etc.)
 * @param {Object} params - Parámetros de filtro opcionales
 * @returns {Promise} Respuesta con el archivo Excel
 */
export const exportarReporte = async (tipo, params = {}) => {
  try {
    const filteredParams = Object.entries(params)
      .filter(([key, value]) => value !== null && value !== undefined && value !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    const queryString = new URLSearchParams(filteredParams).toString();
    const url = `${API_BASE}/exportar/${tipo}/${queryString ? `?${queryString}` : ''}`;
    
    console.log('Exportando reporte:', url);
    return await get(url, { responseType: 'blob' });
  } catch (error) {
    console.error('Error al exportar reporte:', error);
    throw error;
  }
};